<template>
  <b-navbar
    id="topBar"
    toggleable="xxl"
  >
    <section
      id="logo-and-pwa-install-prompt-container"
      class="col-auto"
    >
      <img
        id="logoImg"
        src="@/assets/CATS_Logo.png"
      />
      <div
        name="install-prompt-parent"
        class="install-prompt-parent"
      >
        <InstallPrompt
          v-if="showPwaInstallPrompt"
          id="pwa-install-promotion-component"
          class="speech-bubble-box slide-in-left"
          @install-accepted="onInstallAccepted"
          @install-declined="onInstallDeclined"
        />
      </div>
    </section>
    <BNavbarToggle
      target="top-navbar-toggle-collapse"
      :aria-expanded="panelOpen"
      aria-controls="top-navbar-toggle-collapse"
    ></BNavbarToggle>
    <BCollapse
      id="top-navbar-toggle-collapse"
      ref="navCollapseRef"
      :is-nav="true"
      @click="closeNavPanel"
    >
      <!-- Buttons for navigation -->
      <nav class="col">
        <div id="menuBox">
          <top-menu-box
            @new-trial-clicked="createTrial"
            @user-management-clicked="toggleUserManagementMenu"
          />
        </div>
      </nav>

      <section id="right-nav-section">
        <span
          >Support:&nbsp;
          <a :href="'mailto:' + supportMail"
            >{{ supportMail }} <email-outline class="icon clickable" /></a
        ></span>
      </section>
    </BCollapse>

    <!--
      User info dropdown. It is a direct child of the navbar (not inside the
      collapse) so that it stays visible in the top bar on mobile, where the
      collapse renders as a vertical dropdown sheet below the bar.
    -->
    <div
      id="user-dropdown-btn-container"
      class="col-auto"
    >
      <!-- TODO Change color when dropdown is active -->
      <b-dropdown
        id="user-dropdown-btn"
        no-caret
      >
        <template #button-content>
          <span id="account-dropdown-icons"
            ><account-circle /><MenuDown
          /></span>
        </template>
        <b-dropdown-text id="user-name-text">
          <span
            v-if="keycloak"
            id="user-name-text"
            >{{ keycloak.idTokenParsed?.name }}</span
          >
        </b-dropdown-text>
        <b-dropdown-text>
          <span id="user-role-text">{{ usrRole }}</span>
        </b-dropdown-text>
        <b-dropdown-divider />
        <b-dropdown-item-button
          id="copyTokenBtn"
          class="dropdown-btn"
          @click="copyToken"
        >
          {{ $t('token.copyToken') }}
        </b-dropdown-item-button>
        <b-dropdown-item-button
          id="logoutBtn"
          class="dropdown-btn"
          @click="logout"
        >
          {{ $t('logout') }}
        </b-dropdown-item-button>
      </b-dropdown>
    </div>
  </b-navbar>
</template>

<script lang="ts" setup>
import InstallPrompt from '@/components/Pwa/InstallPrompt.vue';
import AccountCircle from 'vue-material-design-icons/AccountCircle.vue';
import EmailOutline from 'vue-material-design-icons/EmailOutline.vue';
import MenuDown from 'vue-material-design-icons/MenuDown.vue';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/stores/authStore';
import installPromptHelper from '@/helperScripts/pwa/installPromptHelper';
import copyToClipboard from '@/helperScripts/generalUtils/copyToClipboard';
import { useI18n } from 'vue-i18n';
import { useGeneralStore } from '@/stores/generalStore';
import { storeToRefs } from 'pinia';
import TopMenuBox from '@/components/Navigation/TopMenuBox.vue';
import { useRouter } from 'vue-router';
import { onBeforeUnmount, ref, watch } from 'vue';
import type { BCollapse } from 'bootstrap-vue-next';
import config from '@/config';

const { keycloak } = storeToRefs(useAuthStore());
const { usrRole } = storeToRefs(useUserStore());
const { showPwaInstallPrompt } = storeToRefs(useGeneralStore());
const { t } = useI18n();

const router = useRouter();

const supportMail = config.SUPPORT_MAIL;

/**
 * Mobile navigation panel.
 *
 * Below 1400px the navbar collapse (`#top-navbar-toggle-collapse`) is styled
 * as a vertical dropdown sheet below the top bar (see the scoped styles in
 * this component). Opening and closing is managed by the library
 * (BNavbarToggle + BCollapse); the code below only adds the close behaviors
 * expected from a dropdown:
 *  - clicking a panel item closes the panel (handled by the @click on BCollapse)
 *  - any route change closes the panel
 *  - a pointer down outside the top bar closes the panel
 *  - pressing Escape closes the panel
 *
 * Clicking the hamburger itself is handled by the library (BNavbarToggle
 * toggles the collapse). The outside-click handler does not need to special
 * case it, because the toggle button is inside `#topBar` and is therefore
 * ignored by the "outside" check — no close+re-open race can occur.
 */
const navCollapseRef = ref<InstanceType<typeof BCollapse> | null>(null);

/**
 * Whether the mobile navigation panel is open.
 *
 * BCollapse exposes its internal show state as a readonly `visible` ref.
 * Watching that ref (instead of the show/hidden events) is the reliable
 * source of truth: it updates in every environment, including the test
 * environment where the CSS-driven transition events may not fire.
 */
const panelOpen = ref(false);

const closeNavPanel = () => {
  navCollapseRef.value?.hide();
};

const onDocumentPointerDown = (event: PointerEvent) => {
  const target = event.target;
  if (
    target instanceof Node &&
    !document.getElementById('topBar')?.contains(target)
  ) {
    closeNavPanel();
  }
};

const onDocumentKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeNavPanel();
  }
};

//Keep panelOpen in sync with the collapse and only attach the document
//listeners (outside-click / Escape close) while the panel is open
watch(
  () => navCollapseRef.value?.visible ?? false,
  (visible) => {
    panelOpen.value = visible;
    if (visible) {
      document.addEventListener('pointerdown', onDocumentPointerDown);
      document.addEventListener('keydown', onDocumentKeyDown);
    } else {
      document.removeEventListener('pointerdown', onDocumentPointerDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    }
  }
);

//Close the mobile panel whenever the route changes (e.g. after tapping a nav item)
watch(
  () => router.currentRoute.value,
  () => {
    closeNavPanel();
  }
);

//Remove the document listeners if the component is unmounted while the
//panel is open (e.g. in tests), so they do not leak
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown);
  document.removeEventListener('keydown', onDocumentKeyDown);
});

const logout = () => {
  if (keycloak.value) {
    keycloak.value.logout();
    console.log('Ausloggen');
  }
};

const copyToken = () => {
  copyToClipboard.copyToClipboardWithToast(
    keycloak.value?.token,
    t('token.copyTokenMsgTitle'),
    t('token.copyTokenMsg')
  );
};

const onInstallAccepted = () => {
  //User assents to installation
  //Install pwa
  console.log('Installation accepted');
  installPromptHelper.installPromptButtonClicked();
};

const onInstallDeclined = () => {
  //User declines pwa installation
  console.log('Installation declined');
  installPromptHelper.hideInstallPromotion();
  //Save decision to local storage
  installPromptHelper.saveInstallResponseToLocalStorage('dismissed');
};

const createTrial = function () {
  // Open details window and load details
  router.push({ name: 'details', query: { id: 'new' } });
};

const toggleUserManagementMenu = function () {
  // TODO
  alert('Hier wird noch gebaut.');
};
</script>

<style scoped lang="scss">
@import '@/scss/__variables.scss';

#user-name-text {
  color: $default-light-grey;
}

#user-role-text {
  color: $default-light-grey;
  font-size: smaller;
}

//Anchor for the absolutely positioned PWA install prompt speech bubble
.install-prompt-parent {
  position: absolute;
}

#user-dropdown-btn-container {
  //Desktop (>=1400px): the avatar container is a direct child of the navbar.
  //The 1rem left margin reproduces the former 1rem gap inside
  //#right-nav-section, keeping the support mail and the avatar in exactly the
  //same positions as before the avatar was moved out of the navbar collapse.
  margin-left: 1rem;

  :deep(.dropdown-item-text) {
    color: $default-light-grey;
    font-weight: 300;
  }

  :deep(.dropdown-item) {
    color: $default-light-grey;
  }

  :deep(.btn) {
    color: $default-light-grey;
    border: none;
    background: none;
  }
}

#logoImg {
  width: 125px;
}

.speech-bubble-box {
  position: relative;
  background: $vitu-background;
  border-radius: 0.4em;
  box-shadow: 0 0.2em 1.5em -0.1em;
}

.speech-bubble-box:after {
  content: '';
  position: absolute;
  left: 0;
  top: 13%;
  width: 0;
  height: 0;
  border: 0.8em solid transparent;
  border-right-color: $vitu-light-green;
  border-left: 0;
  margin-top: -0.8em;
  margin-left: -0.8em;
}

.slide-in-left {
  //Slide animation for pwa install prompt
  animation: 1s cubic-bezier(0.4, 1.58, 0.66, 0.88) 0s 1 slide-in-left;
}
@-webkit-keyframes slide-in-left {
  0% {
    -webkit-transform: translateX(-100%);
    transform: translateX(-100%);
    visibility: visible;
  }
  100% {
    -webkit-transform: translateX(0);
    transform: translateX(0);
  }
}
@keyframes slide-in-left {
  0% {
    -webkit-transform: translateX(-100%);
    transform: translateX(-100%);
    visibility: visible;
  }
  100% {
    -webkit-transform: translateX(0);
    transform: translateX(0);
  }
}

#logo-and-pwa-install-prompt-container {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  overflow: visible;
  align-items: start;
}

#pwa-install-promotion-component {
  outline: solid $vitu-light-green 2px;
  z-index: 6;
  min-width: 40vw;
  max-width: 100vw;
  background-color: $vitu-background;
  padding: 1em;
  top: 1em;
  left: 5em;
}

#topBar {
  align-items: center;
  background-color: white;
}

#right-nav-section {
  align-items: center;
  gap: 1rem;
  display: flex;
  justify-items: end;

  :deep() {
    color: $default-light-grey;
  }
}

#account-dropdown-icons {
  color: $default-light-grey;
}

#menuBox {
  margin-left: 2rem;
}

//Mobile (<1400px): the navbar collapse renders as a full-width vertical
//dropdown sheet below the fixed-height top bar instead of overflowing
//horizontally inside the bar.
@media screen and (max-width: 1399px) {
  #topBar {
    //Navbar can stretch to the (over-wide) page width when page content
    //overflows horizontally; pin it to the layout viewport so margin-left:auto
    //children (avatar) and the absolute panel resolve against the screen, not
    //the scrolled page. Do NOT add overflow:hidden here — it would clip the
    //dropdown panel (top: 100%).
    max-width: 100vw;
  }

  //:deep() is required here: in this setup (scoped styles + Vue compat
  //mode) the BCollapse root element does not receive this component's
  //scope attribute, so a plain `#top-navbar-toggle-collapse` selector
  //would compile to `#top-navbar-toggle-collapse[data-v-...]` and never
  //match. `:deep()` compiles to `[data-v-...] #top-navbar-toggle-collapse`
  //and matches via the scoped ancestor (NAV#topBar).
  :deep(#top-navbar-toggle-collapse) {
    position: absolute;
    top: 100%;
    left: 0;
    //100vw (layout viewport) instead of `right: 0`: the containing block
    //(NAV#topBar) can be wider than the viewport when page content overflows
    //horizontally, which would stretch the sheet beyond the visible screen
    width: 100vw;
    background-color: white;
    border-bottom: solid 2px $lighter-grey;
    box-shadow: 0 0.5em 1em rgba(0, 0, 0, 0.12);
    //Fallback for browsers without dynamic viewport units
    max-height: calc(100vh - $top-bar-height);
    max-height: calc(100dvh - $top-bar-height);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    z-index: 10;
  }

  #menuBox {
    margin-left: 0;
  }

  //Support mail becomes a full-width row inside the sheet
  #right-nav-section {
    width: 100%;
    padding: 0.75em 1.25em;
    min-height: 44px;
    border-top: solid 2px $lighter-grey;
  }

  //Hamburger and avatar are grouped at the right end of the bar:
  //[logo] .... [hamburger][avatar]. The auto margin pushes the whole group
  //to the right edge; the avatar's base margin-left (1rem) provides the gap
  //between the two. :deep() because the button is the BNavbarToggle
  //component root. (At >=1400px the toggle is display:none via Bootstrap,
  //so this has no effect on the desktop layout.)
  :deep(button.navbar-toggler) {
    margin-left: auto;
  }

  //Avatar stays in the bar: sits right of the hamburger with a 44px touch target
  #user-dropdown-btn-container {
    :deep(.btn) {
      min-width: 44px;
      min-height: 44px;
    }
  }
}
</style>
